import type { KeyboardEvent, MouseEvent } from 'react';

import type { Webcast } from '../../../../../generated/graphql-manager';
import type { MenuPosition } from '../../../../components/menu-more/menu-more';

import {
  ChatIcon,
  DuplicateIcon,
  EventIcon,
  GhostButton,
  IconButton,
  InventoryIcon,
  PlayerIcon,
  Tooltip,
  TrashIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { State } from '../../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../../routes';
import { addParamToPath } from '../../../../../utils';
import { MenuMore } from '../../../../components/menu-more/menu-more';
import { useCurrentUserRolesForWebcast } from '../../../../hooks/use-current-user-roles-for-webcast';
import { useUserPermissions } from '../../../../hooks/user-permissions';
import { ONDEMAND_STATE } from '../../webcasts';

import styles from './action-buttons.module.css';

type Props = {
  webcast: Webcast;
  menuPosition?: MenuPosition;
  onArchiveWebcast: (id: Webcast['id']) => void;
  onDeleteWebcast: (id: Webcast['id']) => void;
  onDuplicateWebcast: (id: Webcast['id'], name: Webcast['name']) => void;
  onUnarchiveWebcast: (id: Webcast['id']) => void;
};

export function ActionButtons({
  webcast,
  menuPosition,
  onArchiveWebcast,
  onDeleteWebcast,
  onDuplicateWebcast,
  onUnarchiveWebcast,
}: Props) {
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const {
    isEventDeletionAllowed,
    isEventDuplicationAllowed,
    isAccessToOperatorAllowed,
    isFullAccessToOperatorAllowed,
    isEventArchivingAllowed,
  } = useUserPermissions();
  const { isOnlyModeratorForEvent } = useCurrentUserRolesForWebcast(webcast.id);

  const { id, name, state, chat, sharingLink } = webcast;
  const isInitializing = state === State.INITIALIZING;
  const isArchivedWebcast = state === State.ARCHIVED;
  const isDeleteWebcastDisabled = state === State.LIVE || state === State.LIVE_TEST || !isEventDeletionAllowed;
  const isDuplicationDisabled = isInitializing || !isEventDuplicationAllowed;
  const isEventArchivingDisabled = state !== State.POSTLIVE && state !== ONDEMAND_STATE;
  const isMessagingAvailable = chat?.applicationId && (isAccessToOperatorAllowed || isOnlyModeratorForEvent);
  const isMoreMenuAvailable =
    isEventDeletionAllowed && isEventDuplicationAllowed && isEventArchivingAllowed && !isOnlyModeratorForEvent;
  const viewerRedirectionParam = sharingLink?.token ? `${id}?hmac-token=${sharingLink.token}` : id;

  const deleteButtonDisabled =
    state === State.LIVE
      ? t('manager.webcasts.actionButtons.deleteDisabledLiveEvent')
      : t('manager.webcasts.actionButtons.deleteDisabledLiveTestEvent');

  const handleKeydown = (event: KeyboardEvent) => {
    event.stopPropagation();
  };

  const handleLinkClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className={styles.actionButtons} onKeyDown={handleKeydown}>
      {!isArchivedWebcast && (
        <>
          {isFullAccessToOperatorAllowed && (
            <Tooltip
              data-testid="webcast-open-operator-button-tooltip"
              label={t('manager.webcasts.actionButtons.operator')}
            >
              <IconButton
                rounded
                component={NavLink}
                to={addParamToPath(routes.operator, id)}
                rel="noreferrer"
                target="_blank"
                aria-label={t('manager.webcasts.actionButtons.operator')}
                data-testid="webcast-open-operator-button"
                onClick={handleLinkClick}
              >
                <EventIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip data-testid="webcast-open-viewer-button-tooltip" label={t('manager.webcasts.actionButtons.viewer')}>
            <IconButton
              rounded
              component={NavLink}
              to={addParamToPath(routes.viewer, viewerRedirectionParam)}
              rel="noreferrer"
              target="_blank"
              aria-label={t('manager.webcasts.actionButtons.viewer')}
              data-testid="webcast-open-viewer-button"
              onClick={handleLinkClick}
            >
              <PlayerIcon />
            </IconButton>
          </Tooltip>

          {isMessagingAvailable && (
            <Tooltip
              data-testid="webcast-open-messaging-button-tooltip"
              label={t('manager.webcasts.actionButtons.messaging')}
            >
              <IconButton
                rounded
                component={NavLink}
                to={addParamToPath(routes.operator_messaging, id)}
                rel="noreferrer"
                target="_blank"
                onClick={handleLinkClick}
                aria-label={t('manager.webcasts.actionButtons.messaging')}
                data-testid="webcast-open-messaging-button"
              >
                <ChatIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}

      {isMoreMenuAvailable && (
        <MenuMore menuPosition={menuPosition}>
          {isEventDuplicationAllowed && (
            <Tooltip
              data-testid="webcast-duplicate-button-tooltip"
              label={t('manager.webcasts.actionButtons.duplicateDisabled')}
              hidden={!isDuplicationDisabled}
            >
              <GhostButton
                small
                onClick={() => onDuplicateWebcast(id, name)}
                disabled={isDuplicationDisabled}
                data-testid="webcast-duplicate-button"
              >
                <DuplicateIcon /> {t('manager.webcasts.actionButtons.duplicate')}
              </GhostButton>
            </Tooltip>
          )}

          {isEventArchivingAllowed && !isArchivedWebcast && (
            <Tooltip
              data-testid="webcast-archive-button-tooltip"
              label={t('manager.webcasts.actionButtons.archiveDisabled')}
              hidden={!isEventArchivingDisabled}
            >
              <GhostButton
                small
                onClick={() => onArchiveWebcast(id)}
                disabled={isEventArchivingDisabled}
                data-testid="webcast-archive-button"
              >
                <InventoryIcon /> {t('manager.webcasts.actionButtons.archive')}
              </GhostButton>
            </Tooltip>
          )}

          {isEventArchivingAllowed && isArchivedWebcast && (
            <GhostButton
              small
              onClick={() => onUnarchiveWebcast(id)}
              disabled={false}
              data-testid="webcast-unarchive-button"
            >
              <InventoryIcon /> {t('manager.webcasts.actionButtons.unarchive')}
            </GhostButton>
          )}

          {isEventDeletionAllowed && (
            <Tooltip
              data-testid="webcast-delete-button-tooltip"
              label={deleteButtonDisabled}
              position="above"
              hidden={!isDeleteWebcastDisabled}
            >
              <GhostButton
                small
                onClick={() => onDeleteWebcast(id)}
                disabled={isDeleteWebcastDisabled}
                data-testid="webcast-delete-button"
              >
                <TrashIcon /> {t('manager.webcasts.actionButtons.delete')}
              </GhostButton>
            </Tooltip>
          )}
        </MenuMore>
      )}
    </div>
  );
}
